/* =====================================================================
 * 어드민 대시보드 엑셀 내보내기 — 시트 구성.
 *
 * 워크북 "데이터"를 만드는 부분과 실제로 파일을 쓰는 부분을 나눠 둔다.
 * 쓰기 쪽은 `xlsx-js-style` 을 동적으로 불러오는데, 이 라이브러리는 앱에서
 * 의도적으로 지연 청크(`vendor-xlsx`)로 분리해 둔 대형 의존성이다
 * (`apps/enterprise/vite.config.ts` 의 manualChunks). 정적 import 를 쓰면
 * 어드민 대시보드를 여는 것만으로 초기 번들에 끌려 들어온다.
 *
 * 시트 이름 규칙은 엑셀이 강제한다 — 아래 `toSheetName` 참조.
 * ===================================================================== */

import type {
  AdminDashboardBreakdown,
  AdminDashboardExport,
  AdminDashboardSummary,
} from '@auw/api-client';

/** 엑셀 시트 이름 상한. 넘기면 파일을 열 때 복구 대화상자가 뜬다. */
const SHEET_NAME_MAX = 31;

/** 엑셀이 시트 이름에 금지하는 문자. */
const SHEET_NAME_FORBIDDEN = /[\\/?*[\]:]/g;

export interface AdminExportLabels {
  summarySheet: string;
  period: string;
  granularity: string;
  totalTokens: string;
  totalRuns: string;
  org: string;
  department: string;
  users: string;
  avgPerRun: string;
  untrackedRuns: string;
  bucket: string;
  unknownOrg: string;
  generatedAt: string;
}

export type SheetRow = (string | number | null)[];

export interface ExportSheet {
  name: string;
  rows: SheetRow[];
}

/**
 * 엑셀이 받아들이는 시트 이름으로 바꾼다.
 *
 * 부서명을 그대로 쓸 수 없는 이유가 여러 개 겹친다: 31자 상한, 금지문자,
 * 그리고 **대소문자 무시 중복 금지**. 잘라내면 앞부분이 같은 부서끼리
 * 충돌하기 쉬워서, 중복은 접미사로 푼다.
 *
 * `used` 는 호출자가 유지하는 집합이며 이 함수가 직접 갱신한다.
 */
export function toSheetName(
  raw: string,
  used: Set<string>,
  fallback = 'sheet',
): string {
  const cleaned = raw.replace(SHEET_NAME_FORBIDDEN, ' ').replace(/\s+/g, ' ').trim();
  // 엑셀은 앞뒤 아포스트로피도 거부한다.
  const base = (cleaned.replace(/^'+|'+$/g, '').trim() || fallback).slice(
    0,
    SHEET_NAME_MAX,
  );

  if (!used.has(base.toLowerCase())) {
    used.add(base.toLowerCase());
    return base;
  }

  for (let n = 2; ; n += 1) {
    const suffix = ` (${n})`;
    const candidate = base.slice(0, SHEET_NAME_MAX - suffix.length) + suffix;
    if (!used.has(candidate.toLowerCase())) {
      used.add(candidate.toLowerCase());
      return candidate;
    }
  }
}

function orgLabel(code: string, name: string | null, unknownOrg: string): string {
  if (name) return name;
  return code === 'unknown' ? unknownOrg : code;
}

/**
 * 워크북 시트 목록: 요약 1장 + 부서별 1장씩.
 *
 * 순서는 화면의 사용량 순위와 같다 — 시트 순서가 표와 다르면 대조가 힘들다.
 *
 * 숫자는 문자열이 아니라 number 로 넣는다. 엑셀에서 합계/피벗을 돌리는 게
 * 내보내기의 목적이고, 문자열로 들어가면 그게 안 된다.
 */
export function buildAdminDashboardSheets(
  data: AdminDashboardExport,
  labels: AdminExportLabels,
  options: {
    summary?: AdminDashboardSummary;
    breakdown?: AdminDashboardBreakdown;
    granularityLabel: string;
    generatedAt: Date;
  },
): ExportSheet[] {
  const period = `${data.start} ~ ${data.end}`;
  const used = new Set<string>();

  /* 부서별 사용자 수는 export 응답에 없다 (일별 행을 합산해 distinct 를 구할
   * 수 없다). 화면이 이미 들고 있는 breakdown 에서 끌어온다. */
  const userCounts = new Map<string, number>();
  for (const org of options.breakdown?.orgs ?? []) {
    for (const dept of org.departments) {
      userCounts.set(`${org.org_code}\u0000${dept.department_code}`, dept.user_count);
    }
  }

  const summaryRows: SheetRow[] = [
    [labels.summarySheet],
    [labels.period, period],
    [labels.granularity, options.granularityLabel],
    [labels.generatedAt, options.generatedAt.toISOString()],
    [labels.totalTokens, data.total_token_count],
    [labels.totalRuns, data.total_runs],
    [],
    [
      labels.org,
      labels.department,
      labels.totalTokens,
      labels.totalRuns,
      labels.users,
      labels.avgPerRun,
      labels.untrackedRuns,
    ],
  ];

  const sheets: ExportSheet[] = [];

  for (const dept of data.departments) {
    const org = orgLabel(dept.org_code, dept.org_name, labels.unknownOrg);
    const name =
      dept.department_name
      ?? (dept.department_code === 'unknown'
        ? labels.unknownOrg
        : dept.department_code);
    const userCount = userCounts.get(`${dept.org_code}\u0000${dept.department_code}`);

    summaryRows.push([
      org,
      name,
      dept.total_token_count,
      dept.total_runs,
      userCount ?? null,
      dept.avg_token_per_run,
      dept.unknown_token_run_count,
    ]);

    sheets.push({
      name: toSheetName(name, used, dept.department_code || 'sheet'),
      rows: [
        [labels.org, org],
        [labels.department, name],
        [labels.period, period],
        [labels.totalTokens, dept.total_token_count],
        [labels.totalRuns, dept.total_runs],
        [],
        [labels.bucket, labels.totalTokens, labels.totalRuns],
        ...dept.points.map((point): SheetRow => [
          point.bucket,
          point.total_token_count,
          point.total_runs,
        ]),
      ],
    });
  }

  /* 요약을 맨 앞에 둔다. 부서 시트 이름을 먼저 확정한 뒤 unshift 하는 이유는
   * 요약 시트 이름이 부서명과 우연히 겹쳐도 부서 쪽을 밀어내지 않도록,
   * 중복 해소를 요약이 마지막에 받게 하기 위해서다. */
  sheets.unshift({
    name: toSheetName(labels.summarySheet, used, 'summary'),
    rows: summaryRows,
  });

  return sheets;
}

/** 파일명. 콜론이 들어가면 일부 OS 에서 저장이 막히므로 날짜만 쓴다. */
export function adminExportFileName(start: string, end: string): string {
  return `sola-usage_${start}_${end}.xlsx`;
}

/** 헤더 행에만 주는 굵게 + 배경. */
const HEADER_STYLE = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'FFF1F1F1' } },
} as const;

/**
 * 시트 목록을 xlsx 파일로 내려준다.
 *
 * `xlsx-js-style` 을 여기서 **동적으로** 불러온다 — 위 파일 주석 참조.
 * 이 함수는 사용자가 내보내기를 누른 뒤에만 실행되므로 그때 받으면 된다.
 */
export async function writeAdminDashboardWorkbook(
  sheets: ExportSheet[],
  fileName: string,
): Promise<void> {
  const XLSX = (await import('xlsx-js-style')).default;
  const book = XLSX.utils.book_new();

  for (const sheet of sheets) {
    const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows);

    /* 표 헤더 행을 굵게. 헤더 위치가 시트마다 다르므로 빈 행의 다음 행으로
     * 잡는다 — 두 시트 모두 메타 블록과 표 사이에 빈 행이 하나 있다. */
    const blankRow = sheet.rows.findIndex((row) => row.length === 0);
    if (blankRow >= 0) {
      const headerRow = blankRow + 1;
      const header = sheet.rows[headerRow] ?? [];
      for (let col = 0; col < header.length; col += 1) {
        const ref = XLSX.utils.encode_cell({ r: headerRow, c: col });
        if (worksheet[ref]) worksheet[ref].s = HEADER_STYLE;
      }
    }

    /* 열 너비. 기본값이면 한글 부서명이 잘려 보인다. */
    const columnCount = Math.max(...sheet.rows.map((row) => row.length), 1);
    worksheet['!cols'] = Array.from({ length: columnCount }, (_, index) => ({
      wch: index === 0 ? 22 : 16,
    }));

    XLSX.utils.book_append_sheet(book, worksheet, sheet.name);
  }

  XLSX.writeFile(book, fileName);
}
